import { useNavigate } from "react-router-dom";
import DataChanneling from "./DataChanneling";
import LiveStreamAppContext from "../ContextStore/Store";
import { appInsightTrack } from "../utils/AppInsights";
import axios from "axios";
import { useContext } from "react";

const EndLiveStream = () => {
  const {
    camera,
    setCamera,
    microphone,
    setMicrophone,
    speaker,
    setSpeaker,
    chatClientRef,
    chatThreadClientRef,
    callN,
    __merchantId,
    __userId,
    __appConfirmUniqueID,
    recordingId,
    setShowInformationStrip,
    setLoaderAnimation,
    role,
    serverCallId,
    roomId,
    apiUrlPost,
    headers,
    setInformationContent,
    isHostNotJoined,
    sessionStartTimeUTC,
    setToggleStates,
  } = useContext(LiveStreamAppContext);

  const navigate = useNavigate();
  const { sendActionToAttendee } = DataChanneling();

  const releaseMemory = async () => {
    try {
      // Clean up device resources
      if (camera) setCamera(null);
      if (microphone) setMicrophone(null);
      if (speaker) setSpeaker(null);

      // Clean up refs - only after we're done using them
      if (chatClientRef.current) chatClientRef.current = null;
      if (chatThreadClientRef.current) chatThreadClientRef.current = null;
      if (callN.current) callN.current = null;
    } catch (error) {
      console.error("Error Releasing Memory:", error);
      appInsightTrack(
        __merchantId,
        __userId,
        "Error Releasing Memory",
        "",
        error
      );
    }
  };

  const endCall = async () => {
    setShowInformationStrip(false);
    setLoaderAnimation(true);

    try {
      setToggleStates((prevStates) => ({
        ...prevStates,
        shareDeviceAudio: false,
      }));

      // If the role is "Presenter", handle the call-ending process
      if (role === "Presenter") {
        // Send action to attendees first
        try {
          sendActionToAttendee("ALL", "MEETING_END");
        } catch (error) {
          console.error("Error sending meeting end action:", error);
        }

        // Wait a bit before ending the call to ensure message is sent
        await new Promise(resolve => setTimeout(resolve, 1000));

        // End the ACS call
        try {
          await endACSCall();
        } catch (error) {
          console.error("Error ending ACS call:", error);
        }

        // Handle recording stop and navigation
        if (serverCallId && recordingId) {
          const payload = {
            appConfirmUniqueId: __appConfirmUniqueID,
            roomId: roomId,
            serverCallId: serverCallId,
            recordingId: recordingId,
            recordingAction: 4,
          };
          // Prepare headers and make the API request to stop the recording
          try {
            await axios.post(`${apiUrlPost}managerecording`, payload, {
              headers,
            });
          } catch (error) {
            console.error("An error occurred", error);
            if (error.status === 400 || error.status === 500) {
              console.error(error.message);
              setShowInformationStrip(true);
              setInformationContent({
                type: "alert",
                message: error.message,
              });
            }
            appInsightTrack(
              __merchantId,
              __userId,
              `API Error managerecording with ${error.status}`,
              "",
              error
            );
          }

          // Redirect based on whether a recording exists
          const currentPath = window.location.pathname;
          const newPath = currentPath.replace(
            "/meeting",
            "/recording-playback"
          );
          navigate(newPath + window.location.search);
        } else {
          // Navigate to host feedback
          const currentPath = window.location.pathname;
          const newPath = currentPath.replace("/meeting", "/feedbackhost");
          navigate(newPath + window.location.search);
        }
      } else {
        // If the role is not "Presenter", just end the ACS call and navigate to feedback
        try {
          await endACSCall();
        } catch (error) {
          console.error("Error ending ACS call:", error);
        }

        // Navigate to feedback page
        const currentPath = window.location.pathname;
        const newPath = currentPath.replace("/meeting", "/feedback");
        navigate(newPath + window.location.search);
      }
    } catch (error) {
      console.error("An error occurred", error);
      appInsightTrack(__merchantId, __userId, "Error at EndCall", "", error);
    } finally {
      // Always hide the loader animation and clean up resources
      setLoaderAnimation(false);
      // Clean up resources after everything is done
      await releaseMemory();
    }
  };

  // Function to handle call hang-up using ACS Method
  const endACSCall = async () => {
    return new Promise((resolve) => {
      // Check if callN ref exists before using it
      if (!callN.current) {
        console.warn("callN ref is null, cannot hang up call");
        resolve();
        return;
      }

      try {
        if (role === "Presenter") {
          callN.current.hangUp({ forEveryone: true });
        } else {
          callN.current.hangUp();
        }
      } catch (error) {
        console.error("Error hanging up call:", error);
      }

      // Small delay to ensure hang up is processed
      setTimeout(() => {
        resolve();
      }, 500);
    });
  };

  return { endCall };
};

export default EndLiveStream;
